import React from 'react'
import { UIRadioGroupBase as RadioGroup, UIRadioGroupItemBase as RadioGroupItem } from './ui-radio-group'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIRadioGroupButtonOption {
  value: string
  label: string
  icon?: React.ReactNode
  id?: string
  disabled?: boolean
  shortcut?: string
}

export interface UIRadioGroupButtonProps extends React.ComponentProps<typeof RadioGroup> {
  options: UIRadioGroupButtonOption[]
  label?: string
  description?: string
  /**
   * 버튼 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 버튼 변형
   */
  variant?: 'default' | 'outline' | 'ghost'
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 필수 여부
   */
  required?: boolean
  /**
   * 전체 너비 사용
   */
  fullWidth?: boolean
}

const sizeVariants = {
  sm: {
    button: 'h-8 px-3 text-xs',
    icon: 'w-3 h-3',
    gap: 'gap-1.5',
    label: 'text-sm leading-4',
    description: 'text-xs leading-4',
    error: 'text-xs leading-4'
  },
  default: {
    button: 'h-9 px-4 text-sm',
    icon: 'w-4 h-4',
    gap: 'gap-2',
    label: 'text-base leading-5',
    description: 'text-sm leading-4',
    error: 'text-sm leading-4'
  },
  lg: {
    button: 'h-10 px-6 text-base',
    icon: 'w-5 h-5',
    gap: 'gap-3',
    label: 'text-lg leading-6',
    description: 'text-base leading-5',
    error: 'text-sm leading-4'
  }
}

const variantStyles = {
  default: {
    base: 'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
    checked: 'bg-primary text-primary-foreground border-primary shadow-sm'
  },
  outline: {
    base: 'border-2 border-muted-foreground bg-transparent hover:bg-accent hover:text-accent-foreground',
    checked: 'border-primary bg-primary text-primary-foreground'
  },
  ghost: {
    base: 'border-transparent bg-transparent hover:bg-accent hover:text-accent-foreground',
    checked: 'bg-primary text-primary-foreground'
  }
}

/**
 * 버튼 스타일 라디오 그룹 컴포넌트
 * 버튼 형태로 선택 옵션을 표시하며, 아이콘과 키보드 단축키를 지원합니다.
 */
export const UIRadioGroupButton = React.forwardRef<
  React.ComponentRef<typeof RadioGroup>,
  UIRadioGroupButtonProps
>(({
  className,
  options,
  label,
  description,
  size = 'default',
  variant = 'default',
  error,
  required = false,
  fullWidth = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}, ref) => {
  const sizeClass = sizeVariants[size]
  const variantClass = variantStyles[variant]
  const [internalValue, setInternalValue] = React.useState(value || defaultValue || '')

  // Update internal value when controlled value changes
  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value || '')
    }
  }, [value])

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  return (
    <UIBox className="space-y-3">
      {label && (
        <UILabel className={cn(
          "font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          sizeClass.label,
          required && "after:content-['*'] after:ml-0.5 after:text-destructive",
          error && "text-destructive"
        )}>
          {label}
        </UILabel>
      )}
      {description && (
        <UITypography
          variant="p"
          color="muted"
          className={sizeClass.description}
        >
          {description}
        </UITypography>
      )}

      <RadioGroup
        ref={ref}
        className={cn(
          "flex flex-wrap",
          sizeClass.gap,
          fullWidth && "grid",
          className
        )}
        style={fullWidth ? { gridTemplateColumns: `repeat(${options.length}, 1fr)` } : undefined}
        value={internalValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {options.map((option, index) => {
          const itemId = option.id || `${props.name || 'button-radio'}-${option.value}-${index}`

          return (
            <UIBox key={option.value} className={cn(fullWidth && "flex")}>
              <RadioGroupItem
                value={option.value}
                id={itemId}
                disabled={option.disabled}
                className="sr-only"
              />
              <UILabel
                htmlFor={itemId}
                className={cn(
                  "inline-flex items-center justify-center rounded-md font-medium transition-[color,background-color,border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
                  sizeClass.button,
                  variantClass.base,
                  fullWidth && "flex-1",
                  option.disabled && "opacity-50 cursor-not-allowed",
                  error && "border-destructive",
                  // 체크된 상태 스타일 - 간단한 조건부 클래스 적용
                  internalValue === option.value && variantClass.checked
                )}
              >
                <UIBox className={cn("flex items-center", sizeClass.gap)}>
                  {option.icon && (
                    <span className={cn("flex-shrink-0", sizeClass.icon)}>
                      {option.icon}
                    </span>
                  )}
                  <UITypography as="span" variant="p" size="sm">{option.label}</UITypography>
                  {option.shortcut && (
                    <kbd className={cn(
                      "ml-auto flex-shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono font-medium text-muted-foreground",
                      size === 'sm' ? 'text-xs' : 'text-xs'
                    )}>
                      {option.shortcut}
                    </kbd>
                  )}
                </UIBox>
              </UILabel>
            </UIBox>
          )
        })}
      </RadioGroup>

      {error && (
        <UITypography
          variant="p"
          color="destructive"
          className={cn("animate-in slide-in-from-top-1 duration-200", sizeClass.error)}
          role="alert"
          as="p"
        >
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

UIRadioGroupButton.displayName = "UIRadioGroupButton"